import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  readonly children: ReactNode;
  readonly fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <Card className="max-w-md">
            <CardContent className="flex flex-col items-center p-6 text-center">
              <AlertTriangle className="h-10 w-10 text-compliance-critical" />
              <h2 className="mt-3 text-lg font-semibold text-foreground">
                {this.props.fallbackTitle ?? 'Something went wrong'}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {this.state.error?.message ?? 'An unexpected error occurred.'}
              </p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => this.setState({ hasError: false, error: null })}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
